import { withListOverflow } from "../../../v2/components/ListOverflow.jsx";
export const OfficeDayDrilldown = withListOverflow(OfficeDayListOwner);
import React, { useEffect, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getDataCoverageOffice } from "../../../data/reportsClient.js";
import { SectionHead, cardStyle, officeIdentity, periodNote, tableStyles } from "./CoverageParts.jsx";
import { fmtCount, fmtDate, fmtDateTime, fmtMonth, fmtRatio, fmtWeekday } from "./coverageFormat.js";

const STATE_TONES = {
  Reported: { bg: T.ragGreenBg, fg: T.ragGreenText },
  "Observed zero": { bg: T.blueSoft, fg: T.hdrFrom },
  Missing: { bg: T.ragAmberBg, fg: T.ragAmberText },
  Excluded: { bg: T.lightGray, fg: T.mid },
};

function OfficeDayListOwner({ month, office, onReturn, returnLabel = "coverage" }) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [attempt, setAttempt] = useState(0);
  const returnRef = useRef(null);

  useEffect(() => { returnRef.current?.focus(); }, []);

  useEffect(() => {
    let live = true;
    setState({ status: "loading", data: null, error: null });
    getDataCoverageOffice({ month, officeId: office.officeId })
      .then((data) => { if (live) setState({ status: "ready", data, error: null }); })
      .catch((error) => { if (live) setState({ status: "error", data: null, error }); });
    return () => { live = false; };
  }, [month, office.officeId, attempt]);

  const data = state.data;
  const o = data?.office ?? office;
  const { th, td } = tableStyles;

  return (
    <section aria-labelledby="dc-drill-title" data-testid="office-drilldown" style={cardStyle}>
      <div style={{ padding: "12px 16px 0" }}>
        <button ref={returnRef} type="button" data-testid="button-drilldown-return" onClick={onReturn}
          style={{ background: "none", border: "none", color: T.accent, fontWeight: T.fwSemibold, fontSize: T.fsSm, cursor: "pointer", padding: 0 }}>
          ‹ Back to {returnLabel}
        </button>
      </div>
      <SectionHead id="dc-drill-title" title={`${officeIdentity(o)} — ${fmtMonth(data?.selectedMonth ?? month)}`}
        right={data ? `As of ${fmtDateTime(data.asOf) ?? "unavailable"} · cutoff ${fmtDate(data.cutoff) ?? "unavailable"} (exclusive)` : null} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 22px", padding: "0 16px 12px", fontSize: T.fsSm, color: T.mid }}>
        <span>Eligible <b style={{ color: T.dark }}>{fmtCount(o.eligibleDays)}</b></span>
        <span>Observed <b style={{ color: T.dark }}>{fmtCount(o.observedDays)}</b></span>
        <span>Missing <b style={{ color: T.ragAmberText }}>{fmtCount(o.missingDays)}</b></span>
        <span>Coverage <b style={{ color: T.dark }}>{fmtRatio(o.coverageRatio)}</b></span>
        {data?.periodState ? <span>Period <b style={{ color: T.dark }}>{data.periodState} · {periodNote(data.periodState, o.isComplete)}</b></span> : null}
      </div>

      {state.status === "loading" && (
        <div role="status" aria-live="polite" data-testid="drilldown-loading" style={{ padding: "0 16px 16px" }}>
          <span style={{ position: "absolute", left: -9999 }}>Loading office days…</span>
          {[0, 1, 2, 3, 4].map((i) => <div key={i} style={{ height: 30, borderRadius: 6, background: T.lightGray, marginBottom: 6 }} />)}
        </div>
      )}
      {state.status === "error" && (
        <div role="alert" data-testid="drilldown-error" style={{ margin: "0 16px 16px", padding: 14, borderRadius: 9, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, color: T.ragRed, fontSize: T.fsSm }}>
          <span data-testid="text-drilldown-error">{state.error?.message || "Office day detail could not be loaded."}</span>{" "}
          <button type="button" data-testid="button-drilldown-retry" onClick={() => setAttempt((n) => n + 1)}
            style={{ background: "none", border: "none", color: T.accent, fontWeight: T.fwSemibold, cursor: "pointer", padding: 0 }}>Retry</button>
        </div>
      )}
      {state.status === "ready" && (!data?.days?.length ? (
        <div data-testid="drilldown-empty" style={{ padding: "0 16px 18px", color: T.mid, fontSize: T.fsSm }}>No days are recorded for this office in this month.</div>
      ) : (
        <div style={{ overflowX: "auto" }} tabIndex={0} role="region" aria-label="Office days, scrolls horizontally">
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead><tr>
              <th style={th} scope="col">Date</th><th style={th} scope="col">State</th>
              <th style={{ ...th, textAlign: "right" }} scope="col">Attendance</th><th style={th} scope="col">Reason</th>
            </tr></thead>
            <tbody>
              {data.days.map((d) => {
                const tone = STATE_TONES[d.state] ?? STATE_TONES.Excluded;
                const needsReason = d.state === "Excluded" || d.state === "Missing";
                return (
                  <tr key={d.date} data-testid={`day-row-${d.date}`} style={{ opacity: d.eligible ? 1 : 0.8 }}>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtWeekday(d.date)} {fmtDate(d.date)}</td>
                    <td style={td}><span style={{ padding: "2px 10px", borderRadius: 6, fontSize: T.fsXs, fontWeight: T.fwSemibold, background: tone.bg, color: tone.fg }}>{d.state}</span></td>
                    <td style={{ ...td, textAlign: "right" }}>{d.attendanceCount === null || d.attendanceCount === undefined ? <span style={{ color: T.light }}>—</span> : fmtCount(d.attendanceCount)}</td>
                    <td style={{ ...td, color: T.mid }}>
                      {d.reason ?? (d.state === "Missing" ? "No observation for this eligible day" : needsReason ? <i>Reason unavailable</i> : "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}
